import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";
import PhoneInput from "../components/PhoneInput";
import TermsCheckbox from "../components/TermsCheckbox";
import SocialAuthButtons from "../components/SocialAuthButtons";
import ShopDetailsFields, { EMPTY_SHOP, shopPayload } from "../components/business/ShopDetailsFields";
import { dialCodeFor } from "../lib/locations";
import { apiErrorMessage } from "../lib/utils";

export default function Register() {
  const { requestSignupCode, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isBusiness = params.get("role") === "business_owner";
  const plan = params.get("plan") === "pro" ? "pro" : "free";
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [shop, setShop] = useState({ ...EMPTY_SHOP, business_type: params.get("type") === "freelance" ? "freelance" : "business" });
  const [whatsappDiffers, setWhatsappDiffers] = useState(false);
  const [payoutDiffers, setPayoutDiffers] = useState(false);
  // Step 2: the code we sent. No account exists until it is entered.
  const [sentTo, setSentTo] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // The account is in the business's name (or the freelancer's).
  const payload = () => {
    const base = { ...form, two_factor_enabled: twoFactorEnabled, accept_terms: acceptTerms };
    if (!isBusiness) return base;
    return {
      ...base,
      full_name: shop.name.trim(),
      role: "business_owner",
      business: shopPayload(shop, { whatsappDiffers, payoutDiffers }),
    };
  };

  const sendCode = async (e) => {
    e?.preventDefault();
    if (isBusiness && shop.business_type === "business" && shop.tin && shop.tin.length !== 9) {
      setError("TIN must be exactly 9 digits.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setSentTo(await requestSignupCode(payload()));
      setCode("");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not send your code. Please check the form and try again."));
    } finally {
      setLoading(false);
    }
  };

  const createAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await register({ ...payload(), code });
      // A business's shop was opened with the account, and is live.
      if (isBusiness) navigate(plan === "pro" ? "/business/subscription" : "/business");
      else navigate(params.get("next") || "/account");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not create your account."));
    } finally {
      setLoading(false);
    }
  };

  if (sentTo) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-16">
        <button
          type="button"
          onClick={() => setSentTo(null)}
          className="mb-4 inline-flex items-center gap-1 self-start text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" /> Change my details
        </button>
        <h1 className="mb-2 font-montserrat text-2xl font-bold text-slate-900">Confirm it's you</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          We sent a 6-digit code to <span className="font-medium text-slate-900">{sentTo}</span>. Enter it to create your
          account{isBusiness ? " and open your shop" : ""}. Check your spam folder if you don't see it.
        </p>
        <form onSubmit={createAccount} className="space-y-4">
          <Input
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-label="Verification code"
            autoFocus
            required
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
            {loading ? "Creating your account…" : isBusiness ? "Create my account and shop" : "Create my account"}
          </Button>
        </form>
        <button type="button" onClick={sendCode} disabled={loading} className="mt-4 text-sm font-medium text-primary hover:underline">
          Send a new code
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-16">
      <h1 className="mb-6 font-montserrat text-2xl font-bold text-slate-900">
        {isBusiness ? "Create your SHUWA account for Business" : "Create your SHUWA account"}
      </h1>
      <SocialAuthButtons onError={setError} onSuccess={() => navigate(isBusiness ? "/business/onboarding" : params.get("next") || "/account")} className="mb-2" />
      <p className="mb-6 text-center text-xs text-muted-foreground">
        By continuing with Google or Apple you accept the SHUWA{" "}
        <a href="/legal/terms" target="_blank" rel="noreferrer" className="underline">Terms and Conditions</a>.
      </p>
      <div className="relative mb-6 flex items-center">
        <div className="h-px flex-1 bg-slate-200" />
        <span className="px-3 text-xs uppercase text-muted-foreground">or</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
      <form onSubmit={sendCode} className="space-y-4">
        {isBusiness && (
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Plan</label>
            <div className="rounded-xl border border-primary bg-primary/5 px-4 py-3 text-left text-sm text-slate-900">
              <span className="block font-semibold">Free</span>
              <span className="block text-xs text-muted-foreground">0 RWF / month</span>
            </div>
            <Link
              to="/for-business/pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-xs font-medium text-primary hover:underline"
            >
              See full plan comparison →
            </Link>
          </div>
        )}
        {isBusiness ? (
          <ShopDetailsFields
            shop={shop}
            onChange={setShop}
            phone={form.phone}
            whatsappDiffers={whatsappDiffers}
            setWhatsappDiffers={setWhatsappDiffers}
            payoutDiffers={payoutDiffers}
            setPayoutDiffers={setPayoutDiffers}
          />
        ) : (
          <Input
            placeholder="Full name"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            required
          />
        )}
        <Input
          type="email"
          placeholder="Email (we'll send you a code)"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
        />
        <PhoneInput
          value={form.phone}
          onChange={(phone) => setForm({ ...form, phone })}
          dial={isBusiness ? dialCodeFor(shop.country) : undefined}
          aria-label="Phone number"
          required
        />
        <Input
          type="password"
          placeholder="Password (min 8 characters)"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          minLength={8}
          required
        />
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-primary/20"
            checked={twoFactorEnabled}
            onChange={(e) => setTwoFactorEnabled(e.target.checked)}
          />
          <span>
            Enable two-factor authentication
            <span className="block text-xs text-muted-foreground">
              We'll text or email you a code each time you log in.
            </span>
          </span>
        </label>
        <TermsCheckbox checked={acceptTerms} onChange={setAcceptTerms} forBusiness={isBusiness} />
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" className="w-full" disabled={loading || !acceptTerms}>
          {loading ? "Sending your code…" : "Sign up"}
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          We'll email you a 6-digit code. Your account is created once you enter it.
        </p>
      </form>
      <p className="mt-4 text-center text-sm text-muted-foreground">
        Already have an account? <Link to="/login" className="font-medium text-primary">Log in</Link>
      </p>
    </div>
  );
}
